import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { Chart, registerables } from "chart.js";
import { formatDate } from "@angular/common";
import { ColumnMode, DatatableComponent } from "@swimlane/ngx-datatable";
import { environment } from "@myenv/environment";
import * as FileSaver from "file-saver";
import { GraphicReportsService } from "../../graphic-reports.service";
import { SharedService } from "@myproject/shared/shared.service";

interface OperationalInsightsDeviceRow {
  serialNumber: string;
  iccid: string;
  vin: string;
  lastContact: string;
  configAge: string;
  latestEvent: string;
  location: string;
}

interface DeviceLiveStatsCounts {
  total: number;
  communicating: number;
  notCommunicating: number;
  disconnected: number;
  notConnected: number;
  deactivated: number;
}

interface StatCardView {
  key: string;
  label: string;
  value: number;
  icon: string;
  variant: "communicating" | "not" | "disconnected" | "never";
}

type StatSelection = "communicating" | "notCommunicating" | "disconnected" | "never";

type NotCommBucketId = "network" | "battery" | "device" | "vehicle" | "others";

@Component({
  selector: "app-operational-insights",
  templateUrl: "./operational-insights.component.html",
  styleUrls: ["./operational-insights.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OperationalInsightsComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly assetBase = "assets/img/reports-svg-icons";
  readonly ColumnMode = ColumnMode;

  @ViewChild("operationalDeviceTable") private operationalDeviceTable?: DatatableComponent;

  /** Dummy fleet list for UI; wire to API when available */
  readonly fleetOptions = [
    "Metro Fleet",
    "Regional Fleet",
    "National Fleet",
    "Last Mile Delivery",
  ];

  fleetControl = new UntypedFormControl("Metro Fleet");

  /** Pie slice order (spec): network, battery, device, vehicle, others */
  private readonly notCommPieOrder: NotCommBucketId[] = ["network", "battery", "device", "vehicle", "others"];

  /** Tab order (spec): Critical Battery, Device Diagnostics, Vehicle Diagnostics, Network / Connectivity, Others */
  readonly notCommTabOrder: NotCommBucketId[] = ["battery", "device", "vehicle", "network", "others"];

  readonly notCommBucketMeta: Record<
    NotCommBucketId,
    { tabLabel: string; pieLabel: string; color: string }
  > = {
    network: {
      tabLabel: "Network / Connectivity",
      pieLabel: "Network",
      color: "#4E8DDE",
    },
    battery: {
      tabLabel: "Critical Battery",
      pieLabel: "Battery",
      color: "#10B77F",
    },
    device: {
      tabLabel: "Device Diagnostics",
      pieLabel: "Device",
      color: "#F59F0A",
    },
    vehicle: {
      tabLabel: "Vehicle Diagnostics",
      pieLabel: "Vehicle",
      color: "#0CC0DF",
    },
    others: {
      tabLabel: "Others",
      pieLabel: "Others",
      color: "#F43E5C",
    },
  };

  private lineChart: any = null;
  private pieChart: any = null;
  private startTime: number;
  private endTime: number;
  private timeZone: string;
  private dateRange = "Last 30 Days";
  private trendColor = "#23923d";

  selectedStat: StatSelection = "communicating";
  notCommActiveBucket: NotCommBucketId = "battery";

  isLoadingLineGraph = true;
  isLoadingDevices = false;
  isLoadingNotCommPie = false;
  isDownloadingCsv = false;
  hideCalendar = false;

  pieLegendItems: { label: string; color: string }[] = [];

  readonly communicatingLegendItems = [
    { label: "Healthy", color: "#4FA650" },
    { label: "Battery", color: "#ef4444" },
    { label: "Device Diagnostics", color: "#eab308" },
    { label: "Vehicle Diagnostics", color: "#f97316" },
  ];

  constructor(
    private reportsService: GraphicReportsService,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    Chart.register(...registerables);
    this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.pieLegendItems = [...this.communicatingLegendItems];
    this.loadDeviceLiveStatsCounts();
    this.loadCommunicatingDevices();
  }

  onTimeChange(e: { startTime: number; endTime: number; dateRange?: string }): void {
    if (e) {
      this.startTime = e.startTime;
      this.endTime = e.endTime;
      if (e.dateRange != null) {
        this.dateRange = e.dateRange;
      }
    }
    this.refreshTimeBoundContent();
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      if (this.selectedStat === "communicating") {
        this.drawCommunicatingPieMock();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyAllCharts();
  }

  selectStatCard(card: StatCardView, event?: Event): void {
    event?.preventDefault();
    const next = this.cardKeyToSelection(card.key);
    if (next === this.selectedStat) {
      return;
    }
    this.selectedStat = next;
    this.destroyAllCharts();
    this.pieLegendItems =
      next === "communicating"
        ? [...this.communicatingLegendItems]
        : next === "notCommunicating"
          ? this.buildNotCommLegendFromBuckets()
          : next === "disconnected"
            ? [{ label: "Disconnected", color: "#F43E5C" }]
            : [];
    setTimeout(() => this.applySelectionDataLoad(), 0);
  }

  onStatCardKeydown(card: StatCardView, ev: KeyboardEvent): void {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      this.selectStatCard(card);
    }
  }

  setNotCommTab(bucket: NotCommBucketId): void {
    if (this.notCommActiveBucket === bucket) {
      return;
    }
    this.notCommActiveBucket = bucket;
    // Tabs are visual only for now; trend and table always show all not-communicating devices.
  }

  isCardSelected(card: StatCardView): boolean {
    return this.cardKeyToSelection(card.key) === this.selectedStat;
  }

  get chartsSectionTitle(): string {
    switch (this.selectedStat) {
      case "communicating":
        return "Communicating Devices";
      case "notCommunicating":
        return "Not Communicating Devices";
      case "disconnected":
        return "Disconnected Devices";
      case "never":
        return "Never Communicated Devices";
      default:
        return "Operational Insights";
    }
  }

  get pieChartTitle(): string {
    switch (this.selectedStat) {
      case "communicating":
        return "Categorized Observations for Communicating Devices";
      case "notCommunicating":
        return "Categorized Observations for Not Communicating Devices";
      case "disconnected":
        return "Disconnected devices";
      default:
        return "";
    }
  }

  get trendChartTitle(): string {
    switch (this.selectedStat) {
      case "communicating":
        return "Communicating Devices 30 Days Trend";
      case "notCommunicating":
        return "Not Communicating Devices Trend";
      case "disconnected":
        return "Disconnected Devices Trend";
      default:
        return "";
    }
  }

  get showDateRangePicker(): boolean {
    return this.selectedStat !== "never";
  }

  get showPieAndTrend(): boolean {
    return this.selectedStat !== "never";
  }

  get disconnectedCount(): number {
    return this.statCards.find((c) => c.key === "disconnected")?.value ?? 0;
  }

  private cardKeyToSelection(key: string): StatSelection {
    switch (key) {
      case "communicating":
        return "communicating";
      case "notCommunicating":
        return "notCommunicating";
      case "disconnected":
        return "disconnected";
      case "notConnected":
        return "never";
      default:
        return "communicating";
    }
  }

  private destroyAllCharts(): void {
    if (this.lineChart) {
      this.lineChart.destroy();
      this.lineChart = null;
    }
    if (this.pieChart) {
      this.pieChart.destroy();
      this.pieChart = null;
    }
  }

  private hasValidTimeRange(): boolean {
    return this.startTime != null && this.endTime != null;
  }

  private applySelectionDataLoad(): void {
    switch (this.selectedStat) {
      case "communicating":
        this.loadCommunicatingDevices();
        this.drawCommunicatingPieMock();
        if (this.hasValidTimeRange()) {
          this.loadCommunicatingTrend();
        }
        break;
      case "notCommunicating":
        this.notCommActiveBucket = "battery";
        this.isLoadingNotCommPie = true;
        queueMicrotask(() => {
          this.drawNotCommunicatingPieMock();
          this.isLoadingNotCommPie = false;
        });
        if (this.hasValidTimeRange()) {
          this.loadNotCommunicatingFull();
        } else {
          this.setOperationalDeviceRows([]);
        }
        break;
      case "disconnected":
        if (this.hasValidTimeRange()) {
          this.drawDisconnectedPie();
          this.loadDisconnectedTrend();
          this.loadDisconnectedDevices();
        } else {
          this.setOperationalDeviceRows([]);
        }
        break;
      case "never":
        this.loadNeverCommunicatedDevices();
        break;
    }
  }

  private refreshTimeBoundContent(): void {
    if (!this.hasValidTimeRange()) {
      return;
    }
    switch (this.selectedStat) {
      case "communicating":
        this.loadCommunicatingTrend();
        break;
      case "notCommunicating":
        this.loadNotCommunicatingFull();
        break;
      case "disconnected":
        this.drawDisconnectedPie();
        this.loadDisconnectedTrend();
        this.loadDisconnectedDevices();
        break;
      default:
        break;
    }
  }

  private buildNotCommLegendFromBuckets(): { label: string; color: string }[] {
    return this.notCommPieOrder.map((id) => ({
      label: this.notCommBucketMeta[id].pieLabel,
      color: this.notCommBucketMeta[id].color,
    }));
  }

  private drawPieChart(
    labels: string[],
    data: number[],
    backgroundColor: string[],
    emptyOk = false
  ): void {
    if (this.pieChart) {
      this.pieChart.destroy();
      this.pieChart = null;
    }
    const ctx = document.getElementById("operationalInsightsPieChart") as HTMLCanvasElement | null;
    if (!ctx) {
      return;
    }
    const total = data.reduce((a, b) => a + b, 0);
    if (!emptyOk && total <= 0) {
      return;
    }
    this.pieChart = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            data: total > 0 ? data : data.map(() => 1),
            backgroundColor,
            borderColor: "#fff",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => {
                const values = item.chart.data.datasets[0].data as number[];
                const sum = values.reduce((a, b) => a + b, 0);
                const v = Number(item.raw);
                const pct = sum ? Math.round((v / sum) * 100) : 0;
                const name = item.label ?? "";
                return `${name}: ${total > 0 ? v : 0} (${total > 0 ? pct : 0}%)`;
              },
            },
          },
        },
      },
    });
  }

  private drawCommunicatingPieMock(): void {
    const labels = ["Healthy", "Battery", "Device Diagnostics", "Vehicle Diagnostics"];
    const data = [92, 3, 3, 2];
    const backgroundColor = ["#4FA650", "#ef4444", "#eab308", "#f97316"];
    this.drawPieChart(labels, data, backgroundColor, true);
  }

  /** Mock slice counts (redesign); replace with live categorization when ready. */
  private drawNotCommunicatingPieMock(): void {
    const labels = this.notCommPieOrder.map((id) => this.notCommBucketMeta[id].pieLabel);
    const data = [28, 12, 22, 18, 8];
    const backgroundColor = this.notCommPieOrder.map((id) => this.notCommBucketMeta[id].color);
    this.pieLegendItems = this.buildNotCommLegendFromBuckets();
    this.drawPieChart(labels, data, backgroundColor, true);
  }

  private drawDisconnectedPie(): void {
    const n = this.disconnectedCount;
    this.pieLegendItems = [{ label: "Disconnected", color: "#647496" }];
    if (n <= 0) {
      if (this.pieChart) {
        this.pieChart.destroy();
        this.pieChart = null;
      }
      return;
    }
    this.drawPieChart(["Disconnected"], [n], ["#647496"], true);
  }

  private loadNotCommunicatingFull(): void {
    if (!this.hasValidTimeRange()) {
      this.setOperationalDeviceRows([]);
      return;
    }
    this.loadNotCommTrendAll();
    this.loadAllNotCommDevicesPaged();
  }

  /** Trend for all not-communicating devices (not filtered by table tab or category). */
  private loadNotCommTrendAll(): void {
    this.isLoadingLineGraph = true;
    const color = "#F6B01D";
    this.reportsService
      .getNotCommTrendData(
        this.startTime,
        this.endTime,
        "groupName",
        "All",
        this.timeZone,
        this.dateRange
      )
      .subscribe({
        next: (res: { type: string; data: { day: number; count: number }[] }[]) => {
          this.isLoadingLineGraph = false;
          if (res?.length) {
            const combined = [{ type: "All", data: this.sumCountsByDay(res) }];
            this.drawLineGraph(combined, color);
          } else {
            this.drawLineGraph([], "");
          }
        },
        error: (error) => {
          this.isLoadingLineGraph = false;
          this.sharedService.getErrorMsg(error);
        },
      });
  }

  private static readonly notCommDevicePageLimit = 100;

  /** All not-communicating devices for the snapshot day; paginates past API page size. */
  private loadAllNotCommDevicesPaged(): void {
    const range = this.computeSnapshotRange();
    if (!range) {
      this.setOperationalDeviceRows([]);
      return;
    }
    this.isLoadingDevices = true;
    const acc: Record<string, unknown>[] = [];
    const limit = OperationalInsightsComponent.notCommDevicePageLimit;
    const loadPage = (page: number): void => {
      this.reportsService.getAllNotCommDevices(range.start, range.end, "All", page, this.timeZone).subscribe({
        next: (res: unknown) => {
          const list = this.normalizeDevicesResponse(res);
          acc.push(...list);
          if (list.length >= limit) {
            loadPage(page + 1);
          } else {
            this.isLoadingDevices = false;
            this.setOperationalDeviceRows(acc.map((d) => this.mapApiDeviceToRow(d)));
          }
        },
        error: (error) => {
          this.isLoadingDevices = false;
          this.setOperationalDeviceRows([]);
          this.sharedService.getErrorMsg(error);
        },
      });
    };
    loadPage(0);
  }

  private loadDisconnectedTrend(): void {
    this.isLoadingLineGraph = true;
    this.reportsService
      .getDisconnectedTrendData(
        this.startTime,
        this.endTime,
        "groupName",
        "All",
        this.timeZone,
        this.dateRange
      )
      .subscribe({
        next: (res: { type: string; data: { day: number; count: number }[] }[]) => {
          this.isLoadingLineGraph = false;
          if (res?.length) {
            const combined = [{ type: "All", data: this.sumCountsByDay(res) }];
            this.drawLineGraph(combined, "#F43E5C");
          } else {
            this.drawLineGraph([], "");
          }
        },
        error: (error) => {
          this.isLoadingLineGraph = false;
          this.sharedService.getErrorMsg(error);
        },
      });
  }

  private computeSnapshotRange(): { start: number; end: number } | null {
    if (!this.hasValidTimeRange()) {
      return null;
    }
    const selectedDate = new Date(this.endTime);
    if (this.endTime - this.startTime >= 86400000 && this.dateRange !== "Custom") {
      selectedDate.setDate(selectedDate.getDate() - 1);
    }
    selectedDate.setHours(0, 0, 0, 0);
    const start = selectedDate.getTime() + selectedDate.getTimezoneOffset() * -1 * 60 * 1000;
    return { start, end: start };
  }

  private loadDisconnectedDevices(): void {
    const range = this.computeSnapshotRange();
    if (!range) {
      this.setOperationalDeviceRows([]);
      return;
    }
    this.isLoadingDevices = true;
    this.reportsService.getAllDisconnectedDevices(range.start, range.end, "All", 0, this.timeZone).subscribe({
      next: (res: unknown) => {
        this.isLoadingDevices = false;
        const list = this.normalizeDevicesResponse(res);
        this.setOperationalDeviceRows(list.map((d) => this.mapApiDeviceToRow(d)));
      },
      error: (error) => {
        this.isLoadingDevices = false;
        this.setOperationalDeviceRows([]);
        this.sharedService.getErrorMsg(error);
      },
    });
  }

  private loadNeverCommunicatedDevices(): void {
    this.isLoadingDevices = true;
    this.reportsService.getDevicesWithLiveStatusType(0, "NeverCommunicating").subscribe({
      next: (res: unknown) => {
        this.isLoadingDevices = false;
        const list = this.normalizeDevicesResponse(res);
        this.setOperationalDeviceRows(list.map((d) => this.mapApiDeviceToRow(d)));
      },
      error: (error) => {
        this.isLoadingDevices = false;
        this.setOperationalDeviceRows([]);
        this.sharedService.getErrorMsg(error);
      },
    });
  }

  private loadCommunicatingDevices(): void {
    this.isLoadingDevices = true;
    this.reportsService.getDevicesWithLiveStatusType(0, "Communicating").subscribe({
      next: (res: unknown) => {
        this.isLoadingDevices = false;
        const list = this.normalizeDevicesResponse(res);
        this.setOperationalDeviceRows(list.map((d) => this.mapApiDeviceToRow(d)));
      },
      error: (error) => {
        this.isLoadingDevices = false;
        this.setOperationalDeviceRows([]);
        this.sharedService.getErrorMsg(error);
      },
    });
  }

  private normalizeDevicesResponse(res: unknown): Record<string, unknown>[] {
    if (Array.isArray(res)) {
      return res as Record<string, unknown>[];
    }
    if (res && typeof res === "object" && "data" in res && Array.isArray((res as { data: unknown }).data)) {
      return (res as { data: Record<string, unknown>[] }).data;
    }
    return [];
  }

  private mapApiDeviceToRow(d: Record<string, unknown>): OperationalInsightsDeviceRow {
    const lastComm = d["lastCommunication"] as string | undefined;
    let lastContact = "";
    if (lastComm) {
      try {
        lastContact = formatDate(lastComm, environment.largeDateFormat, "en-US");
      } catch {
        lastContact = "";
      }
    }
    const lat = d["latitude"] as number | null | undefined;
    const lng = d["longitude"] as number | null | undefined;
    const latestEvent =
      (d["lastKnownEvent"] as string | undefined) ??
      (d["lastKnownStatus"] as string | undefined) ??
      "";
    return {
      serialNumber: this.asString(d["serialNumber"]),
      iccid: this.asString(d["simCcid"]),
      vin: this.asString(d["vin"]),
      lastContact,
      configAge: "",
      latestEvent,
      location: this.formatLocation(lat, lng),
    };
  }

  private asString(value: unknown): string {
    if (value === null || value === undefined) {
      return "";
    }
    return String(value);
  }

  private formatLocation(lat: number | null | undefined, lng: number | null | undefined): string {
    if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) {
      return "";
    }
    return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
  }

  private loadDeviceLiveStatsCounts(): void {
    this.reportsService.getAzugaLiveStatus().subscribe({
      next: (res: DeviceLiveStatsCounts) => {
        if (!res) {
          return;
        }
        this.statCards = [
          {
            key: "communicating",
            label: "Communicating",
            value: res.communicating ?? 0,
            icon: "icon-wifi.svg",
            variant: "communicating",
          },
          {
            key: "notCommunicating",
            label: "Not Communicating",
            value: res.notCommunicating ?? 0,
            icon: "icon-warning.svg",
            variant: "not",
          },
          {
            key: "disconnected",
            label: "Disconnected",
            value: res.disconnected ?? 0,
            icon: "icon-wifi-off.svg",
            variant: "disconnected",
          },
          {
            key: "notConnected",
            label: "Never Communicated",
            value: res.notConnected ?? 0,
            icon: "icon-signal-disconnected.svg",
            variant: "never",
          },
        ];
        if (this.selectedStat === "disconnected") {
          queueMicrotask(() => this.drawDisconnectedPie());
        }
      },
      error: (error) => {
        this.sharedService.getErrorMsg(error);
      },
    });
  }

  private sumCountsByDay(
    dataSets: { type: string; data: { day: number; count: number }[] }[]
  ): { day: number; count: number }[] {
    const sumByDayMap = new Map<number, number>();
    dataSets.forEach((dataSet) => {
      dataSet.data.forEach((item) => {
        const currentCount = sumByDayMap.get(item.day) || 0;
        sumByDayMap.set(item.day, currentCount + item.count);
      });
    });
    return Array.from(sumByDayMap, ([day, count]) => ({ day, count }));
  }

  private loadCommunicatingTrend(): void {
    if (!this.hasValidTimeRange()) {
      return;
    }
    this.isLoadingLineGraph = true;
    this.trendColor = "#23923d";
    this.reportsService
      .getCommTrendData(
        this.startTime,
        this.endTime,
        "groupName",
        "All",
        this.timeZone,
        this.dateRange
      )
      .subscribe({
        next: (res: { type: string; data: { day: number; count: number }[] }[]) => {
          this.isLoadingLineGraph = false;
          if (res?.length) {
            const combined = [{ type: "All", data: this.sumCountsByDay(res) }];
            this.drawLineGraph(combined, this.trendColor);
          } else {
            this.drawLineGraph([], "");
          }
        },
        error: (error) => {
          this.isLoadingLineGraph = false;
          this.sharedService.getErrorMsg(error);
        },
      });
  }

  private drawLineGraph(
    res: { type: string; data: { day: number; count: number }[] }[],
    color: string
  ): void {
    if (this.lineChart) {
      this.lineChart.destroy();
      this.lineChart = null;
    }
    const labels: string[] = [];
    const dataset: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      fill: boolean;
    }[] = [];
    let highestCount = 0;
    if (res.length) {
      res[0].data.forEach((item) => {
        try {
          const eventDate = formatDate(item.day, "dd MMM yyyy", "en-US", "UTC");
          labels.push(eventDate);
        } catch {
          labels.push("");
        }
      });
      const highestCountsArray: number[] = [];
      res.forEach((item) => {
        const counts: number[] = [];
        item.data.forEach((innerItem) => {
          counts.push(innerItem.count);
        });
        const rowMax = counts.length ? Math.max(...counts) : 0;
        highestCountsArray.push(rowMax);
        dataset.push({
          label: item.type,
          data: counts,
          backgroundColor: color,
          borderColor: color,
          fill: false,
        });
      });
      highestCount = highestCountsArray.length ? Math.max(...highestCountsArray) : 0;
    }
    const ctx = document.getElementById("operationalInsightsLineChart") as HTMLCanvasElement | null;
    if (!ctx) {
      return;
    }
    this.lineChart = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: dataset,
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            position: "right",
            display: false,
          },
        },
        scales: {
          x: {
            display: true,
            beginAtZero: true,
          },
          y: {
            display: true,
            stacked: false,
            beginAtZero: true,
            suggestedMin: 0,
            suggestedMax: 10,
            ticks: {
              stepSize: highestCount > 9 ? undefined : 1,
            },
          },
        },
      },
    });
  }

  statCards: StatCardView[] = [
    {
      key: "communicating",
      label: "Communicating",
      value: 0,
      icon: "icon-wifi.svg",
      variant: "communicating",
    },
    {
      key: "notCommunicating",
      label: "Not Communicating",
      value: 0,
      icon: "icon-warning.svg",
      variant: "not",
    },
    {
      key: "disconnected",
      label: "Disconnected",
      value: 0,
      icon: "icon-wifi-off.svg",
      variant: "disconnected",
    },
    {
      key: "notConnected",
      label: "Never Communicated",
      value: 0,
      icon: "icon-signal-disconnected.svg",
      variant: "never",
    },
  ];

  deviceRows: OperationalInsightsDeviceRow[] = [];

  activeDeviceTab: "healthy" | "attention" = "healthy";

  /** Assigns table rows and resets client-side pager offset so an empty or shorter list is not stuck on a past page. */
  private setOperationalDeviceRows(rows: OperationalInsightsDeviceRow[]): void {
    this.deviceRows = rows;
    queueMicrotask(() => {
      if (this.operationalDeviceTable) {
        this.operationalDeviceTable.offset.set(0);
      }
    });
  }

  asset(path: string): string {
    return `${this.assetBase}/${path}`;
  }

  downloadDeviceData(): void {
    const liveStatusType = this.liveStatusTypeForSelection(this.selectedStat);
    if (!liveStatusType) {
      return;
    }
    this.isDownloadingCsv = true;
    this.reportsService.downloadDeviceCSVWithLiveStatusType({}, liveStatusType).subscribe({
      next: (res) => {
        this.isDownloadingCsv = false;
        const contentDisposition = res.headers.get("Content-Disposition");
        if (contentDisposition == null) {
          const decoder = new TextDecoder("utf-8");
          const text = decoder.decode(res.body);
          this.sharedService.setResponseMessage({ success: true, message: text });
        } else {
          const filename = contentDisposition.split(";")[1].split("filename")[1].split("=")[1].trim();
          const blob = new Blob([res.body], { type: "text/csv;charset=utf-8" });
          FileSaver.saveAs(blob, filename);
        }
      },
      error: (error) => {
        this.isDownloadingCsv = false;
        this.sharedService.getErrorMsg(error);
      },
    });
  }

  private liveStatusTypeForSelection(sel: StatSelection): string | null {
    switch (sel) {
      case "communicating":
        return "Communicating";
      case "notCommunicating":
        return "NotCommunicating";
      case "disconnected":
        return "Disconnected";
      case "never":
        return "NeverCommunicating";
      default:
        return null;
    }
  }
}
